import { nextTick, watch } from 'vue';
import type { Router } from 'vue-router';
import { tenantUnavailable } from './tenantAvailability';

// The boot frame's exit (issue 533). Installed once per app in main.ts, right
// after `app.mount()`: `releaseBootFrame(router)`.
//
// Each app's index.html paints `#lumi-boot` — AppShell's silhouette, styled by
// component-lib's bootFrame.css — OUTSIDE `#app`, over the page, so a full page
// load (a module, role or demo persona switch) shows the shell's frame instead of
// an empty lavender screen. It must stay until the app can show what the frame
// stands in for, and that is the FIRST RESOLVED ROUTE, not the mount: the router's
// guards read the session, the onboarding status and the active role before the
// first view renders, and vue-router loads the view's lazy chunk only after them.
// Until then `<RouterView>` renders nothing — and where the shell is itself a route
// component (backoffice, time, system-admin), nothing at all.
//
// So the frame lifts once `router.isReady()` settles AND that render has been
// flushed. It also lifts when the navigation fails (the router has nothing more to
// wait for, and the error is the app's to show) and when the workspace turns out to
// be unreachable (App.vue's TenantUnavailableView replaces the whole app, router or
// not). A guard that leaves for the workspace login never resolves; the frame then
// stays until the browser unloads the page, which is what it should do.

export const BOOT_FRAME_ID = 'lumi-boot';

/** Long enough for the CSS fade (`--dur-fast`); a fallback, since a reduced-motion
 * visitor gets no transition and therefore no `transitionend`. */
const REMOVE_AFTER_MS = 400;

export function releaseBootFrame(router: Pick<Router, 'isReady'>, doc: Document = document): void {
  const frame = doc.getElementById(BOOT_FRAME_ID);
  if (!frame) return;

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    stopWatching();
    // `--leaving` drops the frame's pointer capture at once and fades it over the
    // real shell, which by now stands right under it in the same geometry.
    frame.classList.add('lumi-boot--leaving');
    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      frame.remove();
    };
    frame.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, REMOVE_AFTER_MS);
  };

  const stopWatching = watch(tenantUnavailable, (unavailable) => {
    if (unavailable) void nextTick(release);
  });
  if (tenantUnavailable.value) {
    void nextTick(release);
    return;
  }
  router.isReady().then(
    () => nextTick(release),
    () => nextTick(release),
  );
}
